import * as React from 'react';

import { useWindowSize } from 'utils/hooks/useWindowSize';

import VatixIcon from './components/Icons/VatixIcon';

import {
  SideBarProducts,
  LogoItem,
  StyledLine,
  SubSideBarProducts,
  CompanyLogoContainer,
  SubSideBarTitle,
  SideBarContainer,
  SubItemsList,
} from './styles';
import ProductItem from './components/ProductItem';
import { ProductType } from './types';
import SubItems from './components/SubItems';

const findSelectedInNestedPaths = (
  arr: ProductType[],
  targetTo: string,
  parent = undefined
): ProductType | undefined => {
  for (const obj of arr) {
    if (obj.to === targetTo) {
      return parent;
    }

    if (obj.subItems && Array.isArray(obj.subItems)) {
      // @ts-ignore
      const nestedResult = findSelectedInNestedPaths(obj.subItems, targetTo, obj);
      if (nestedResult) {
        return obj;
      }
    }
  }
  return undefined;
};

const NewSideBar: React.FunctionComponent<{
  Products: ProductType[];
  current: string;
  collapsed: boolean;
  hasOrganisationLogo: boolean;
}> = ({ Products, current, collapsed, hasOrganisationLogo }) => {
  let selectedObject = Products.find((i) => i.to === current || i.isSelected);
  if (selectedObject === undefined) {
    const result = findSelectedInNestedPaths(Products, current);
    if (result !== undefined) {
      selectedObject = result;
    } else {
      selectedObject = Products.find((i) => i.to !== '/dashboard/' && current.startsWith(i.to));
    }
  }
  const { height: sideBarHeight } = useWindowSize();

  // Dynamically find the index of the "Actions" menu item
  // The separation line should appear after Actions, or after Schedules if it comes after Actions
  const actionsIndex = Products.findIndex((product) => product.name === 'Actions');
  const schedulesIndex = Products.findIndex((product) => product.name === 'Schedules');

  // If Schedules exists and comes after Actions, place the line after Schedules
  // Otherwise, place it after Actions (if Actions exists)
  let indexOfSeparationLine = 0;
  if (actionsIndex >= 0) {
    if (schedulesIndex > actionsIndex) {
      indexOfSeparationLine = schedulesIndex;
    } else {
      indexOfSeparationLine = actionsIndex;
    }
  }

  return (
    <SideBarContainer height={sideBarHeight}>
      <SideBarProducts data-testid="sidebar-products">
        <LogoItem id="vatix-logo">
          <VatixIcon />
        </LogoItem>

        {Products.map(({ name, tooltipTitle, Icon, ActiveIcon, to, isSelected, hideIcon, testId }, id) => {
          if (hideIcon) {
            return null;
          }
          return (
            <React.Fragment key={`${id}-${to}`}>
              <ProductItem
                name={name}
                tooltipTitle={tooltipTitle}
                Icon={Icon}
                ActiveIcon={ActiveIcon}
                to={to}
                isSelected={isSelected}
                testId={testId}
              />
              {id === indexOfSeparationLine && <StyledLine />}
            </React.Fragment>
          );
        })}
      </SideBarProducts>

      <SubSideBarProducts data-testid="subsidebar-products" $collapsed={collapsed}>
        <CompanyLogoContainer $collapsed={collapsed}>
          {hasOrganisationLogo && (
            <img id="company-logo" alt="" style={{ maxHeight: '30px' }} src="./transparent.png" />
          )}
        </CompanyLogoContainer>

        <SubItemsList $collapsed={collapsed}>
          {selectedObject?.name && <SubSideBarTitle>{selectedObject?.name}</SubSideBarTitle>}
          <SubItems subItems={selectedObject?.subItems} />
          {selectedObject?.customContent}
        </SubItemsList>
      </SubSideBarProducts>
    </SideBarContainer>
  );
};

export default NewSideBar;
